import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { useAuth, API } from "@/contexts/AuthContext";
import axios from "axios";
import { Bell, Calendar, Crown, ChevronRight, Plus, CalendarPlus, Settings, AlertCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { formatDateBR, formatCivilDateBR, todayCivilSP } from "@/lib/dates";
import { phase1Api } from "@/lib/phase1Api";
import { DADOS } from "../lib/larguras";
import InstalarApp from "../components/InstalarApp";
const DashboardCharts = React.lazy(() => import("@/components/DashboardCharts"));

function StatCard({ label, value, link, urgent, testId }) {
  return (
    <Link
      to={link}
      data-testid={testId}
      className={`block bg-white rounded-lg p-5 border transition-all duration-200 hover:border-[#003882] hover:-translate-y-0.5 ${urgent ? "border-[#003882]" : "border-[#D1DEEE]"}`}
    >
      <p className="text-[#335480] text-sm font-medium mb-1">{label}</p>
      <h4
        className={`text-3xl font-bold ${urgent ? "text-[#003882]" : "text-[#001533]"}`}
        style={{ fontFamily: "Cabinet Grotesk" }}
      >
        {value ?? "—"}
      </h4>
    </Link>
  );
}

function tipoColor(tipo) {
  const t = (tipo || "").toLowerCase();
  if (t.includes("audiência") || t.includes("audiencia")) return "status-audiencia";
  if (t.includes("prazo")) return "status-prazo";
  if (t.includes("reunião") || t.includes("reuniao")) return "status-reuniao";
  return "status-outros";
}

function MorningGroup({ title, items, type, urgent = false }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className={`text-sm font-semibold ${urgent ? "text-[#003882]" : "text-[#335480]"}`}>{title}</h3>
        <span className="text-xs text-[#6685AD]">{items.length}</span>
      </div>
      {items.length === 0 ? <p className="text-sm text-[#6685AD]">Nada por aqui.</p> : (
        <div className="space-y-1.5">
          {items.slice(0, 4).map((item, index) => {
            const taskId = item.card_id || item.task_id;
            const link = type === "task" && taskId ? `/quadros?card_id=${encodeURIComponent(taskId)}` : type === "intimation" ? "/intimacoes" : "/agenda";
            return <Link key={taskId || item.intimacao_id || item.event_id || index} to={link} className="block rounded-md px-3 py-2 bg-[#F4F7FB] hover:bg-[#e5edf5] transition-colors">
              <p className="text-sm font-medium text-[#001533] truncate">{item.title || item.titulo || item.numero_processo || "Item sem título"}</p>
              <p className="text-xs text-[#6685AD] truncate">{item.processo_numero || item.responsavel_nome || item.data_limite || item.data_inicio || "Abrir detalhes"}</p>
            </Link>;
          })}
        </div>
      )}
    </div>
  );
}

function formatDashboardEventDate(event) {
  if (event?.source === "task" || event?.all_day || /^\d{4}-\d{2}-\d{2}$/.test(event?.data_inicio || "")) return formatCivilDateBR(event.data_inicio);
  try { return format(new Date(event.data_inicio), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }); } catch { return event?.data_inicio || "—"; }
}

function dashboardEventSortValue(event) {
  if (event?.source === "task" || event?.all_day || /^\d{4}-\d{2}-\d{2}$/.test(event?.data_inicio || "")) return String(event.data_inicio || "");
  const timestamp = new Date(event?.data_inicio).getTime();
  return Number.isNaN(timestamp) ? Number.MAX_SAFE_INTEGER : timestamp;
}

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [morning, setMorning] = useState({ overdue: [], dueToday: [], events: [], untriaged: [], loading: true, errors: [] });

  const loadStats = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/payments/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (e) {
      console.error(e);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStats();
  }, []);

  useEffect(() => {
    let active = true;
    const today = todayCivilSP();
    Promise.allSettled([
      phase1Api.getMorning(),
      phase1Api.getUpcomingAgenda(),
      phase1Api.getUntriagedIntimations(),
    ]).then(([work, agenda, intimations]) => {
      if (!active) return;
      const workData = work.status === "fulfilled" ? (work.value.data || {}) : {};
      const events = agenda.status === "fulfilled" ? (Array.isArray(agenda.value.data) ? agenda.value.data : []).filter((event) => String(event.data_inicio || "").slice(0, 10) === today) : [];
      const intimationData = intimations.status === "fulfilled" ? intimations.value.data : {};
      setMorning({
        overdue: workData.atrasadas || workData.overdue || [],
        dueToday: workData.vencem_hoje || workData.due_today || [],
        events: workData.eventos_hoje || workData.events_today || events,
        untriaged: workData.intimacoes_nao_triadas || workData.untriaged || (intimationData.items || []),
        loading: false,
        errors: [work, agenda, intimations].reduce((sum, result) => sum + (result.status === "rejected" ? 1 : 0), 0),
      });
    });
    return () => { active = false; };
  }, []);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Bom dia";
    if (h < 18) return "Boa tarde";
    return "Boa noite";
  };

  const firstName = user?.name?.split(" ")[0] || "Advogado";

  return (
    <Layout>
      <div className={`${DADOS} space-y-8`}>
        {/* Header */}
        <div className="animate-fade-in-1">
          <p className="text-[#335480] text-sm font-medium mb-1">
            {format(new Date(), "EEEE, d 'de' MMMM 'de' yyyy", { locale: ptBR })}
          </p>
          <h1 className="text-4xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
            {greeting()}, {firstName}.
          </h1>
          {stats?.workspace_name && (
            <p className="text-[#335480] text-lg mt-1">{stats.workspace_name}</p>
          )}
        </div>

        <InstalarApp />

        {/* PRO Banner */}
        {stats?.plan === "free" && (
          <div className="animate-fade-in-2 bg-[#e5edf5] border border-[#003882]/20 rounded-lg p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown size={20} className="text-[#003882]" />
              <p className="text-[#001533] font-medium text-base">
                Você está no plano <strong>FREE</strong>. Faça upgrade para processos e usuários ilimitados.
              </p>
            </div>
            <Link to="/plano">
              <Button
                data-testid="upgrade-banner-btn"
                className="bg-[#003882] hover:bg-[#00285e] text-white font-semibold text-sm"
              >
                Ver planos
              </Button>
            </Link>
          </div>
        )}

        {/* Error Banner */}
        {error && (
          <div className="bg-[#e5edf5] border border-[#003882] rounded-lg px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-[#001533] text-sm font-medium">
              <AlertCircle size={16} className="text-[#003882]" />
              Não foi possível carregar os dados. Verifique sua conexão.
            </div>
            <button
              onClick={loadStats}
              className="text-sm text-[#003882] font-semibold underline hover:text-[#00285e] transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        )}

        {/* Minha manhã — leitura operacional, com o Kanban como fonte das tarefas */}
        <section className="bg-white border border-[#D1DEEE] rounded-lg animate-fade-in-2" aria-labelledby="morning-title">
          <div className="px-6 py-4 border-b border-[#D1DEEE] flex items-center justify-between">
            <h2 id="morning-title" className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>Minha manhã</h2>
            {!morning.loading && morning.errors > 0 && <span className="text-xs text-[#6685AD]">Alguns dados não carregaram</span>}
          </div>
          {morning.loading ? (
            <div className="p-6 space-y-3"><div className="h-5 bg-[#F4F7FB] rounded animate-pulse" /><div className="h-12 bg-[#F4F7FB] rounded animate-pulse" /></div>
          ) : (morning.overdue.length + morning.dueToday.length + morning.events.length + morning.untriaged.length) === 0 ? (
            <div className="p-8 text-center text-[#335480]"><p className="font-medium">Sua manhã está em dia.</p><p className="text-sm mt-1">Novas tarefas e intimações aparecerão aqui.</p></div>
          ) : (
            <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-[#D1DEEE]">
              <div className="p-6 space-y-4">
                <MorningGroup title="Atrasadas" items={morning.overdue} type="task" urgent />
                <MorningGroup title="Vencem hoje" items={morning.dueToday} type="task" />
                <MorningGroup title="Intimações para triagem" items={morning.untriaged} type="intimation" />
              </div>
              <div className="p-6"><MorningGroup title="Eventos de hoje" items={morning.events} type="event" /></div>
            </div>
          )}
        </section>

        {/* First-run onboarding */}
        {!loading && !error && stats?.oabs_count === 0 && (
          <div className="bg-white border border-[#003882] rounded-lg p-6">
            <h3 className="text-[#001533] font-semibold text-lg mb-2" style={{ fontFamily: "Cabinet Grotesk" }}>
              Bem-vindo ao TUTO — comece em 3 passos
            </h3>
            <ol className="text-[#335480] text-base space-y-1 mb-4 list-none">
              <li className="flex items-start gap-2">
                <span className="text-[#003882] font-bold mt-0.5">1.</span>
                Acesse <Link to="/configuracoes" className="text-[#003882] underline font-medium">Configurações</Link> e cadastre seu número de OAB
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#003882] font-bold mt-0.5">2.</span>
                Sincronizamos seus processos automaticamente
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#003882] font-bold mt-0.5">3.</span>
                Acompanhe intimações e prazos aqui no painel
              </li>
            </ol>
            <Link
              to="/configuracoes"
              className="inline-flex items-center gap-2 bg-[#003882] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#00285e] transition-colors"
            >
              <Settings size={16} />
              Ir para Configurações
            </Link>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in-2">
          <StatCard
            label="Processos ativos"
            value={loading ? "—" : stats?.processos_count ?? 0}
            link="/processos"
            testId="stat-processos"
          />
          <StatCard
            label="Intimações pendentes"
            value={loading ? "—" : stats?.intimacoes_pendentes ?? 0}
            link="/intimacoes"
            urgent={stats?.intimacoes_pendentes > 0}
            testId="stat-intimacoes"
          />
          <StatCard
            label="Próximos eventos"
            value={loading ? "—" : stats?.proximos_eventos_count ?? 0}
            link="/agenda"
            testId="stat-agenda"
          />
          <StatCard
            label="OABs cadastradas"
            value={loading ? "—" : stats?.oabs_count ?? 0}
            link="/configuracoes"
            testId="stat-oabs"
          />
        </div>

        {/* Ações rápidas */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-fade-in-2">
          {[
            { icon: Plus, label: "Novo processo", to: "/processos" },
            { icon: Bell, label: "Ver intimações", to: "/intimacoes" },
            { icon: CalendarPlus, label: "Novo evento", to: "/agenda" },
            { icon: Settings, label: "Cadastrar OAB", to: "/configuracoes" },
          ].map((a, i) => (
            <Link
              key={a.label || i}
              to={a.to}
              data-testid={`quick-action-${i}`}
              className="flex items-center gap-3 bg-white border border-[#D1DEEE] rounded-lg px-4 py-3 hover:border-[#003882] hover:bg-[#F4F7FB] transition-all"
            >
              <div className="w-9 h-9 rounded-lg bg-[#e5edf5] text-[#003882] flex items-center justify-center flex-shrink-0">
                <a.icon size={18} strokeWidth={1.5} />
              </div>
              <span className="text-[#001533] text-sm font-medium leading-tight">{a.label}</span>
            </Link>
          ))}
        </div>

        {/* Gráficos (lazy — recharts em chunk separado) */}
        {!loading && (
          <React.Suspense fallback={
            <div className="grid md:grid-cols-2 gap-6">
              {[1,2,3,4].map(i => (
                <div key={i} className="bg-white border border-[#D1DEEE] rounded-lg p-5">
                  <div className="h-4 w-40 bg-[#F4F7FB] rounded animate-pulse mb-4" />
                  <div className="h-[200px] bg-[#F4F7FB] rounded animate-pulse" />
                </div>
              ))}
            </div>
          }>
            <DashboardCharts charts={stats?.charts} />
          </React.Suspense>
        )}

        <div className="grid lg:grid-cols-2 gap-6 animate-fade-in-3">
          {/* Recent Intimações */}
          <div className="bg-white border border-[#D1DEEE] rounded-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#D1DEEE]">
              <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                Intimações recentes
              </h2>
              <Link
                to="/intimacoes"
                className="text-[#003882] text-sm font-medium hover:underline flex items-center gap-1"
                data-testid="view-all-intimacoes"
              >
                Ver todas <ChevronRight size={14} />
              </Link>
            </div>
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-[#F4F7FB] rounded animate-pulse" />
                ))}
              </div>
            ) : (stats?.recentes_intimacoes?.length ?? 0) === 0 ? (
              <div className="p-8 text-center text-[#335480]">
                <Bell size={32} className="mx-auto mb-3 opacity-40" />
                <p>Nenhuma intimação recente</p>
              </div>
            ) : (
              <div className="divide-y divide-[#D1DEEE]">
                {stats.recentes_intimacoes.map((intimacao, i) => (
                  <Link key={intimacao.id || i} to="/intimacoes" className="block px-6 py-4 hover:bg-[#F4F7FB] transition-colors" data-testid={`intimacao-item-${i}`}>
                    <p className="text-[#001533] text-base font-medium truncate">
                      {intimacao.numero_processo || "Processo não informado"}
                    </p>
                    <p className="text-[#335480] text-sm truncate mt-0.5">
                      {intimacao.tipo_comunicacao} — {formatDateBR(intimacao.data_disponibilizacao)}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Próximos Eventos */}
          <div className="bg-white border border-[#D1DEEE] rounded-lg">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#D1DEEE]">
              <h2 className="text-xl font-bold text-[#001533]" style={{ fontFamily: "Cabinet Grotesk" }}>
                Próximos eventos
              </h2>
              <Link
                to="/agenda"
                className="text-[#003882] text-sm font-medium hover:underline flex items-center gap-1"
                data-testid="view-all-agenda"
              >
                Ver agenda <ChevronRight size={14} />
              </Link>
            </div>
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-12 bg-[#F4F7FB] rounded animate-pulse" />
                ))}
              </div>
            ) : (stats?.eventos_recentes?.length ?? 0) === 0 ? (
              <div className="p-8 text-center text-[#335480]">
                <Calendar size={32} className="mx-auto mb-3 opacity-40" />
                <p>Nenhum evento cadastrado</p>
              </div>
            ) : (
              <div className="divide-y divide-[#D1DEEE]">
                {[...(stats.eventos_recentes || [])].sort((a, b) => String(dashboardEventSortValue(a)).localeCompare(String(dashboardEventSortValue(b)))).map((evt, i) => (
                  <Link key={evt.id || i} to="/agenda" className="block px-6 py-4 hover:bg-[#F4F7FB] transition-colors" data-testid={`evento-item-${i}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-[#001533] text-base font-medium">{evt.titulo}</p>
                      <span className={`text-sm px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${tipoColor(evt.tipo)}`}>
                        {evt.tipo}
                      </span>
                    </div>
                    <p className="text-[#335480] text-sm mt-0.5">
                      {formatDashboardEventDate(evt)}
                    </p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
